import React from "react";
import {Button, Modal, notification} from "antd";
import {EditOutlined, InfoCircleOutlined, LoadingOutlined, PlusOutlined} from "@ant-design/icons";
import {unstable_batchedUpdates} from "react-dom";
import {Cell, EditCell} from "@q4us-sw/q4us-ui/lib/Cell";
import {CustomFormInput} from "@q4us-sw/q4us-ui/lib/Form";
import {insertNewRefTableData, submitRefTableData, visualizeWithFilters} from "../api/api";
import {Form} from "@q4us-sw/q4us-ui";
import {validateData} from "./validation"
import {isEmpty, validateURL} from "../utils/util";


export const MerchantInfoContext = React.createContext()
export const EditorContext = React.createContext()
export const AddNewRecordContext = React.createContext()
export const CellActionContext = React.createContext()
export const GroupValueContext = React.createContext()

export const EditButton = ({data}) => {
    return <GroupValueContext.Consumer>
        {
            group => <CellActionContext.Consumer>
                {
                    cell => <EditorContext.Consumer>
                        {
                            edit => <Button
                                type="link"
                                icon={<EditOutlined/>}
                                onClick={e => {
                                    unstable_batchedUpdates(() => {
                                        edit?.setVisible(true)
                                        edit?.setConfigs(cell?.config)
                                        edit?.setValue(cell?.data?.[cell?.config?.name])
                                        edit?.setRow(cell?.data)
                                        edit?.setSchema(cell?.schema)
                                        edit?.setGroupName(group?.name)
                                    })
                                }}
                            />
                        }
                    </EditorContext.Consumer>
                }
            </CellActionContext.Consumer>
        }
    </GroupValueContext.Consumer>
}


export const AddNewRecordContextProvider = (props) => {

    const [visible, setVisible] = React.useState(false)
    const [config, setConfig] = React.useState(false)

    return <AddNewRecordContext.Provider value={{setVisible, setConfig}}>
        <Modal
            centered
            closable
            visible={visible}
            footer={null}
            title={`Add New ${config?.[0]?.table_name}`}
            onCancel={() => setVisible(false)}
        >
            <MerchantInfoContext.Consumer>
                {em => <Form schema={`add new `} config={async () => {
                    return config;
                }} submit={async (values) => {
                    const [row] = config || [];
                    const {table_id} = row || {}
                    if (table_id) {
                        switch (table_id) {
                            case "em_establishment":
                                await insertNewRefTableData({...values, em_id: em?.data?.id}, 'em_establishment')
                                break;
                            case "em_vat":
                                await insertNewRefTableData({...values, em_id: em?.data?.id}, 'em_vat')
                                break;
                            case "em_site":
                                await insertNewRefTableData({...values, em_id: em?.data?.id}, 'em_site')
                                break;
                            case "em_non_eu_vat_registration":
                                if(values.use_eas_vat_registration){
                                    values.vat_id = null;
                                    values.registered_date = null;
                                    values.registered_name = null;
                                    values.registered_address = null;
                                    notification.warn({ message: 'Using EAS VAT Registration details', description: 'Self VAT registration information for the same country is cleared', duration: 0 })
                                }
                                else{
                                    if(!values.use_eas_vat_registration){
                                        values.use_eas_vat_registration = false

                                        if(!(values.vat_id && values.registered_date)){
                                            notification.error({message: 'Incomplete VAT Registration data', description: 'Please set VAT ID and Registration date for EM VAT registration (or use EAS registration)', duration: 0})
                                            return
                                        }
                                    }
                                }
                                await insertNewRefTableData({...values, em_id: em?.data?.id}, 'em_non_eu_vat_registration')
                                break;
                            default:
                                break;

                        }
                    }
                    const {data: {rows: [r] = []} = {}} = await visualizeWithFilters('em', [{
                        key: 'id',
                        value: em?.data?.id
                    }]) || {}
                    if (r)
                        em?.setData(r)
                    setVisible(false)
                }}
                validateField={async  (rule, value, schema, column, form) => {
                    switch (column.name){
                        case "web_address":{
                            if (!isEmpty(form.getFieldsValue(['web_address'])['web_address']))
                                if (!validateURL(form.getFieldsValue(['web_address'])['web_address']))
                                    return Promise.reject(`Enter a valid [${column.title}]`)
                        }
                    }
                }}
                />}
            </MerchantInfoContext.Consumer>

        </Modal>
        {props.children}
    </AddNewRecordContext.Provider>
}


export const EditorContextProvider = (props) => {
    const [loading, setLoading] = React.useState(false)
    const [visible, setVisible] = React.useState(false)
    const [configs, setConfigs] = React.useState()
    const [value, setValue] = React.useState()
    const [row, setRow] = React.useState()
    const [schema, setSchema] = React.useState()
    const [groupName, setGroupName] = React.useState()

    return <EditorContext.Provider
        value={{
            visible,
            setVisible,
            configs,
            setConfigs,
            value,
            setValue,
            setRow,
            row,
            schema,
            setSchema,
            setGroupName,
            groupName
        }}>
        {visible && <Modal
            closable={false}
            title={null}
            visible={visible}
            footer={null}
            className={"ant-modal ant-modal-confirm ant-modal-confirm-confirm"}
        >
            <div className="ant-modal-confirm-body-wrapper">
                <div className="ant-modal-confirm-body">
                    <InfoCircleOutlined/>
                    <span
                        className="ant-modal-confirm-title">{`Change [${configs?.title}] for ${groupName || "EM"}`}</span>
                    <div className="ant-modal-confirm-content">
                        <div className={`em-editor-dialog`}>
                            <div className={`title`}>{configs?.title}</div>
                            <div className={`edit-cell`}>
                                <CustomFormInput
                                    schema={'em'}
                                    type={configs?.type}
                                    name={configs?.name}
                                    title={configs?.title}
                                    {...configs}
                                    value={configs?.type === 'LIST_MULTIPLE' ? (value != null ? value.map(o=>`${o}`) : []) : value}
                                    onChange={e => {
                                    setValue(e)
                                }}/>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="ant-modal-confirm-btns em-editor-btn-wrap">
                    <MerchantInfoContext.Consumer>
                        {em => <Button icon={loading ? <LoadingOutlined/> : undefined} className="em-editor-approve-btn"
                                       onClick={async () => {
                                           setLoading(true)
                                           if (row?.id) {
                                               const validate = await validateData(configs, value, em);

                                               if(validate){
                                                   switch (configs?.table_id) {
                                                       case "em_establishment":
                                                           await submitRefTableData({
                                                               id: row.id,
                                                               [configs?.name]: value != null ? value : null
                                                           }, 'em_establishment', {emptyStringAllowed: true});
                                                           break;
                                                       case "em_vat": {
                                                           if (row.type === 'fixed_establishment') {
                                                               const fieldName = configs?.name === 'vat_id' ? "vat_number" : configs?.name;
                                                               await submitRefTableData({
                                                                   id: row.id,
                                                                   [fieldName]: value != null ? value : null
                                                               }, 'em_establishment', {emptyStringAllowed: true});
                                                           } else {
                                                               await submitRefTableData({
                                                                   id: row.id,
                                                                   [configs?.name]: value != null ? value : null
                                                               }, 'em_vat', {emptyStringAllowed: true});
                                                           }
                                                       }
                                                           break;
                                                       case "em_site":
                                                           await submitRefTableData({
                                                               id: row.id,
                                                               [configs?.name]: value != null ? value : null
                                                           }, 'em_site', {emptyStringAllowed: true});
                                                           break;
                                                       case "em_non_eu_vat_registration":
                                                           if(configs?.name === 'use_eas_vat_registration' && !value){
                                                               if(!(row.vat_id && row.registered_date)){
                                                                   notification.error({message: 'Incomplete VAT Registration data', description: 'Please set VAT ID and Registration date before switching to EM VAT registration', duration: 0})
                                                                   return
                                                               }
                                                           }
                                                           await submitRefTableData({
                                                               id: row.id,
                                                               [configs?.name]: value != null ? value : null
                                                           }, 'em_non_eu_vat_registration', {emptyStringAllowed: false});
                                                           break;
                                                       case "em_oss_info":
                                                           if(configs?.name === 'ioss_registered' && value === false){
                                                               const data = [
                                                                   {id: row.id, ioss_registered: false},
                                                                   {id: row.id, ioss_registry_import_scheme_id: null},
                                                                   {id: row.id, ioss_import_scheme_registered_country: null},
                                                                   {id: row.id, ioss_import_scheme_request_date: null},
                                                                   {id: row.id, ioss_import_scheme_commencement_date: null} ];
                                                               data.map(async(o)=> {
                                                                     await submitRefTableData(o, 'em', {emptyStringAllowed: true});
                                                                   })
                                                           }
                                                           else {
                                                               await submitRefTableData({
                                                                   id: row.id,
                                                                   [configs?.name]: value != null ? value : null
                                                               }, 'em', {emptyStringAllowed: true});
                                                           }
                                                           break;
                                                       default: {
                                                           await submitRefTableData({
                                                               id: row.id,
                                                               [configs?.name]: value != null ? value : null
                                                           }, 'em', {emptyStringAllowed: true});
                                                       }
                                                   }
                                               }

                                               const {data: {rows: [r] = []} = {}} = await visualizeWithFilters('em', [{
                                                   key: 'id',
                                                   value: em?.data?.id
                                               }]) || {}
                                               if (r)
                                                   em?.setData(r)
                                           }

                                           unstable_batchedUpdates(() => {
                                               setVisible(false)
                                               setLoading(false)
                                               setValue(undefined)
                                               setRow(undefined)
                                               setGroupName(undefined)
                                               setConfigs(undefined)
                                           })
                                       }}>Yes</Button>}
                    </MerchantInfoContext.Consumer>
                    <Button className={`ant-btn-dangerous`} onClick={() => {
                        unstable_batchedUpdates(() => {
                            setVisible(false)
                            setLoading(false)
                            setRow(undefined)
                            setGroupName(undefined)
                            setValue(undefined)
                            setConfigs(undefined)
                        })
                    }}>No</Button>

                </div>
            </div>
        </Modal>}
        {props.children}
    </EditorContext.Provider>
}
